import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSiteTypeLabel } from '../../../../constants/labels/site';
import { siteTypeEnum } from '../../../../constants/site';
import { useCreateSiteMutation, useUpdateSiteMutation } from '../../../../hooks/mutations/useSiteMutation';
import { useWorkTravelZonesQuery } from '../../../../hooks/queries/useWorkTravelZonesQuery';
import { useGeocodeQuery } from '../../../../hooks/queries/useGeocodingQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { Site } from '../../../../services/apiSLTP/planning';
import { Entity } from '../../../../services/apiSLTP/sites/entities';
import { User } from '../../../../services/apiSLTP/users';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { crudStatus } from '../../../../utils/enums';
import { getDistanceBetweenAddresses } from '../../../../utils/numbers';
import CustomDateInput from '../../../CustomDateInput';
import CustomTextInput from '../../../CustomTextInput';
import DropDown, { OptionType } from '../../../DropDown';
import ToggleLine from '../../../ToggleLine';
import ModalBody from '../../ModalBody';
import { useBoolean } from '../../../../hooks/useBoolean';
import { LinearProgress } from '@mui/material';

interface ModalProps {
    site: Site | null;
    users: User[];
    entities: Entity[];
    showEditModal: boolean;
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateSiteModal: React.FC<ModalProps> = ({ site, showEditModal, setShowEditModal, users, entities, isEditMode = true, refetch }) => {
    const { t } = useTranslation();

    const [name, setName] = useState(site?.name || '');
    const [codeProC, setCodeProC] = useState(site?.codeProC || '');
    const [address, setAddress] = useState(site?.address || '');
    const [debouncedAddress, setDebouncedAddress] = useState(site?.address || '');
    const [latitude, setLatitude] = useState(site?.latitude?.toString() || '');
    const [longitude, setLongitude] = useState(site?.longitude?.toString() || '');
    const [crudStatusState, setCrudStatusState] = useState(site?.crudStatus || crudStatus.OK);
    const [type, setType] = useState<OptionType>(
        site?.type
            ? { label: capitalizeFirstLetter(getSiteTypeLabel(site.type)), value: site.type.toString() }
            : { label: capitalizeFirstLetter(getSiteTypeLabel(siteTypeEnum.INTERNAL)), value: siteTypeEnum.INTERNAL.toString() }
    );

    const [isVisibleInApp, setIsVisibleInApp] = useState(site?.isVisibleInApp ?? true);
    const [selectedUser, setSelectedUser] = useState<OptionType | null>(null);
    const [selectedAssistantConduc, setSelectedAssistantConduc] = useState<OptionType | null>(null);
    const [selectedEntity, setSelectedEntity] = useState({ label: '', value: '' });
    const [selectedTravelZone, setSelectedTravelZone] = useState({ label: '', value: '' });
    const [startDate, setStartDate] = useState(site?.startDate ? new Date(site.startDate) : null);
    const [endDate, setEndDate] = useState(site?.endDate ? new Date(site.endDate) : null);

    const [distanceKm, setDistanceKm] = useState<number | null>(null);

    const loadingDistance = useBoolean(false);

    const { user } = useAuth();

    const { data: dataTravelZones, isLoading: isTravelZonesLoading } = useWorkTravelZonesQuery({ queries: { active: true } });

    const { data: coords, isLoading: isGeocoding } = useGeocodeQuery({
        address: debouncedAddress,
    });

    const {
        mutate: mutateSite,
        isSuccess: isSuccessSite,
        isError: isErrorSite,
        isLoading: isLoadingSite,
        error: errorSite,
        data: dataSite,
    } = useUpdateSiteMutation({
        onSuccess: () => {
            refetch();
        },
        onError: (error) => {
            console.error('Error updating site:', error.message);
        },
    });

    const {
        mutate: addSite,
        isSuccess: isSuccessAdd,
        isError: isErrorAdded,
        isLoading: isLoadingAdded,
        error: errorAdded,
        data: dataAdded,
    } = useCreateSiteMutation({
        onSuccess: () => {
            refetch();
        },
        onError: (error) => {
            console.error('Error adding site:', error.message);
        },
    });

    const handleActiveChange = (isActive: boolean) => {
        setCrudStatusState(isActive ? crudStatus.OK : crudStatus.INACTIVE);
    };

    const handleVisibleInAppChange = (checked: boolean) => {
        setIsVisibleInApp(checked);
    };

    // Debounce de l'adresse pour éviter trop d'appels API
    const debouncedSetAddress = useCallback(
        debounce((value: string) => {
            setDebouncedAddress(value);
        }, 500),
        []
    );

    // Calcul de la distance quand les coordonnées changent
    const computeDistance = useCallback(async () => {
        if (!coords || !entities?.length || !debouncedAddress.length || !dataTravelZones?.data.length) {
            setDistanceKm(null);
            return;
        }

        loadingDistance.setTrue();

        try {
            const distances = await Promise.all(
                entities
                    .filter((e) => e.address)
                    .map(async (e) => {
                        const distance = await getDistanceBetweenAddresses(e.address, debouncedAddress);
                        return distance !== null ? { entity: e, distance } : null;
                    })
            );

            const validDistances = distances.filter(Boolean) as { entity: any; distance: number }[];

            if (validDistances.length === 0) {
                setDistanceKm(null);
                return;
            }

            const closest = validDistances.reduce((min, current) => (current.distance < min.distance ? current : min));

            setDistanceKm(closest.distance);

            const matchedZone = dataTravelZones.data.find((zone) => closest.distance >= zone.minDistance && closest.distance < zone.maxDistance);

            if (matchedZone) {
                setSelectedTravelZone({ label: matchedZone.name, value: matchedZone._id });
            }
        } catch (error) {
            console.error('Erreur lors du calcul de distance:', error);
        } finally {
            loadingDistance.setFalse();
        }
    }, [coords, entities, debouncedAddress, dataTravelZones]);

    useEffect(() => {
        if (coords) {
            setLatitude(coords.lat.toString());
            setLongitude(coords.lon.toString());
        }
    }, [coords]);

    useEffect(() => {
        computeDistance();
    }, [computeDistance]);

    useEffect(() => {
        debouncedSetAddress(address);
    }, [address, debouncedSetAddress]);

    useEffect(() => {
        const user = users.find((value) => value?._id === site?.conducId?._id);
        if (user) setSelectedUser({ label: formatName(user.firstName, user.lastName), value: `${user._id}` });
        const assistantConduc = users.find((value) => value?._id === site?.assistantConducId?._id);
        if (assistantConduc)
            setSelectedAssistantConduc({ label: formatName(assistantConduc.firstName, assistantConduc.lastName), value: assistantConduc._id });
        const entity = entities.find((value) => value?._id === site?.entityId);
        if (entity) setSelectedEntity({ label: entity?.designation, value: `${entity?._id}` });
        const travelZone = dataTravelZones?.data.find((value) => value?._id === site?.workTravelZoneId._id);
        if (travelZone) setSelectedTravelZone({ label: travelZone?.name, value: `${travelZone?._id}` });
    }, [site, users, entities, dataTravelZones]);

    return (
        <ModalBody
            title={t('site')}
            subTitle={isEditMode ? t('update') : t('add')}
            closeText={t('cancel')}
            okText={t('validate')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disabledOk={
                !address.length || !codeProC.length || !name.length || !selectedEntity.value.length || !selectedTravelZone.value.length || !type
            }
            handleOk={() => {
                if (isEditMode) {
                    mutateSite({
                        siteId: site?._id || '',
                        data: {
                            address,
                            codeProC,
                            name,
                            crudStatus: crudStatusState,
                            conducId: selectedUser?.value || null,
                            assistantConducId: selectedAssistantConduc?.value || null,
                            entityId: selectedEntity.value || '',
                            type: parseInt(type.value),
                            isVisibleInApp,
                            startDate: startDate?.toISOString(),
                            endDate: endDate?.toISOString(),
                            workTravelZoneId: selectedTravelZone.value,
                            latitude: latitude ? parseFloat(latitude) : null,
                            longitude: longitude ? parseFloat(longitude) : null,
                        },
                    });
                } else {
                    addSite({
                        address,
                        codeProC,
                        name,
                        conducId: selectedUser?.value || null,
                        assistantConducId: selectedAssistantConduc?.value || null,
                        entityId: selectedEntity.value,
                        startTime: new Date(new Date().setHours(7)).toISOString(),
                        type: parseInt(type.value),
                        isVisibleInApp,
                        startDate: startDate?.toISOString(),
                        endDate: endDate?.toISOString(),
                        workTravelZoneId: selectedTravelZone.value,
                        latitude: latitude ? parseFloat(latitude) : undefined,
                        longitude: longitude ? parseFloat(longitude) : undefined,
                    });
                }
            }}
        >
            <CustomTextInput value={name} label={t('name')} onChange={(text) => setName(text)} errorText={t('error')} required />
            <CustomTextInput value={codeProC} label={t('codeProC')} onChange={(text) => setCodeProC(text)} errorText={t('error')} required />
            <ToggleLine checked={crudStatusState === crudStatus.OK} onChange={handleActiveChange} title={capitalizeFirstLetter(t('active'))} />
            <DropDown
                items={Object.values(siteTypeEnum).map((type) => ({ label: capitalizeFirstLetter(getSiteTypeLabel(type)), value: type.toString() }))}
                selectedItem={type}
                handleChange={(event, value) => {
                    if (value) setType(value);
                }}
                inputLabel={t('type')}
                required
            />
            <DropDown
                items={entities.map((entity) => ({ label: entity.designation, value: entity._id }))}
                selectedItem={selectedEntity}
                handleChange={(event, value) => setSelectedEntity({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('entity')}
                required
            />
            <CustomTextInput value={address} onChange={(text) => setAddress(text)} label={t('address')} errorText={t('error')} required />
            <DropDown
                items={dataTravelZones?.data.map((entity) => ({ label: entity.name, value: entity._id })) || []}
                selectedItem={selectedTravelZone}
                handleChange={(event, value) => setSelectedTravelZone({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('workTravelZone')}
                required
                helperText={distanceKm !== null ? `${t('distance')}: ${distanceKm.toFixed(2)} km` : undefined}
                loading={isGeocoding || loadingDistance.value}
            />
            {(isGeocoding || loadingDistance.value) && <LinearProgress />}
            <CustomTextInput value={latitude} label={t('latitude')} onChange={(text) => setLatitude(text)} type="number" />
            <CustomTextInput value={longitude} label={t('longitude')} onChange={(text) => setLongitude(text)} type="number" />

            <DropDown
                items={users.map((user) => ({ label: formatName(user.firstName, user.lastName), value: user._id }))}
                selectedItem={selectedUser}
                handleChange={(event, value) => setSelectedUser({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('conduc')}
                disableClearable={false}
            />
            <DropDown
                items={users.map((user) => ({ label: formatName(user.firstName, user.lastName), value: user._id }))}
                selectedItem={selectedAssistantConduc}
                handleChange={(event, value) => setSelectedAssistantConduc(value)}
                inputLabel={t('assistantConduc')}
                disableClearable={false}
            />

            <ToggleLine checked={isVisibleInApp} onChange={handleVisibleInAppChange} title={capitalizeFirstLetter(t('visibleInNeeds'))} />
            <CustomDateInput
                label={t('startDate')}
                onChange={(value) => (value ? setStartDate(new Date(value?.toISOString())) : null)}
                defaultDate={startDate}
            />
            <CustomDateInput
                label={t('endDate')}
                onChange={(value) => (value ? setEndDate(new Date(value?.toISOString())) : null)}
                defaultDate={endDate}
            />
        </ModalBody>
    );
};

export default UpdateSiteModal;
